import { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { TrackData } from "../context/types";
import { saveTemporaryTrack } from "../api/track";

export interface SaveTemporaryTrackParams {
    token: string;
    trackData: {
        internalId: number;
        trackId: number;
        cityId: number;
        description: string;
        duration: number;
        guideId: number;
        highlights: string;
        language: string;
        length: number;
        remoteAccess: boolean;
        subtitle: string;
        title: string;
        type: string;
        price: number;
        promotion: boolean;
        photos: string[];
        countryId: number;
        position: number;
        aigeneration: string;
        excelPath: string;
        numberOfWords: number;
        theme: string;
        audience: string;
        languages: string[];
        isDialog: boolean;
        isGenerateTextOnly: boolean;
        isGenerateTextAndAudio: boolean;
        voice: string;
        voiceSecond: string;
        unlockModeFree: boolean;
        museumName: string;
        isMuseumList: boolean;
        museumPictureUrl: string;
        museumInfo: string;
        forceDownload: "none" | "recommend" | "force";
        museumSearchQrcode: string;
        subType: string;
        startingLat: number | null;
        startingLng: number | null;
        startingAddress: string;
        ratingLink: string;
        numberOfListeningsBeforeRating: number;
        durationText?: string | null;
        departureText?: string | null;
    };
}

type UpdateTrackData = (field: keyof TrackData, value: any) => void;

export const useSaveTrack = (trackData: TrackData, token: string, updateTrackData: UpdateTrackData) => {
    const navigate = useNavigate();
    const location = useLocation();

    const saveTrack = useCallback(async (): Promise<string | null> => {
        let trackId: string | null = null;

        if (trackData.id && trackData.type && trackData.language) {
            try {
                const trackPayload = {
                    internalId: trackData.id,
                    trackId: trackData.trackId,
                    cityId: trackData.city, // make sure your field names match what the API expects
                    description: trackData.description,
                    duration: trackData.duration,
                    guideId: trackData.guide,
                    highlights: trackData.highlights,
                    language: trackData.language,
                    length: trackData.length,
                    remoteAccess: trackData.remoteAccess,
                    subtitle: trackData.subtitle,
                    title: trackData.title,
                    type: trackData.type,
                    price: trackData.price,
                    promotion: trackData.promotion,
                    photos: trackData.photos,
                    countryId: trackData.country,
                    position: trackData.position,
                    aigeneration: trackData.generationOption,
                    excelPath: trackData.excelPath,
                    numberOfWords: trackData.numberOfWords,
                    theme: trackData.theme,
                    audience: trackData.audience,
                    languages: trackData.languages,
                    isDialog: trackData.isDialog,
                    isGenerateTextOnly: trackData.isGenerateTextOnly,
                    isGenerateTextAndAudio: trackData.isGenerateTextAndAudio,
                    voice: trackData.voice,
                    voiceSecond: trackData.voiceSecond,
                    unlockModeFree: trackData.unlockModeFree,
                    museumName: trackData.museumName,
                    isMuseumList: trackData.isMuseumList,
                    museumPictureUrl: trackData.museumPictureUrl,
                    museumInfo: trackData.museumInfo,
                    forceDownload: trackData.forceDownload,
                    museumSearchQrcode: trackData.museumSearchQrcode,
                    subType: trackData.subType,
                    startingLat: trackData.startingLat,
                    startingLng: trackData.startingLng,
                    startingAddress: trackData.startingAddress,
                    ratingLink: trackData.ratingLink,
                    numberOfListeningsBeforeRating: trackData.numberOfListeningsBeforeRating,
                    isStartAnywhere: trackData.isStartAnywhere,
                    isWithoutRoutes: trackData.isWithoutRoutes,
                    isWithoutMap: trackData.isWithoutMap,
                    hasProgressionSystem: trackData.hasProgressionSystem,
                    displayWaypointIndexNumbers: trackData.displayWaypointIndexNumbers,
                    hideTrackRatings: trackData.hideTrackRatings,
                    defaultTriggerMode: trackData.defaultTriggerMode,
                    durationText: trackData.durationText,
                    departureText: trackData.departureText,
                    // Note: hasSocialSharePopup and socialShareImage are intentionally NOT included here
                    // They are managed exclusively via the /tracks/:trackId/social-share endpoint
                    // to prevent accidental overwrites when saving track data
                };

                const response = await saveTemporaryTrack({
                    token,
                    trackData: trackPayload,
                });

                if (!response) {
                    console.error("Error saving temporary track:");
                } else if (response.trackId) {
                    trackId = response.trackId;

                    if (!trackData.trackId) {
                        updateTrackData("trackId", response.trackId);
                        const currentPathEnd = location.pathname.split("/").pop();
                        const newUrl = `/create/tour/${response.trackId}/${currentPathEnd}`;
                        navigate(newUrl);
                    }
                } else {
                    trackId = trackData.trackId ?? null;
                }
            } catch (error) {
                console.error("Error saving temporary track:", error);
            }
        }

        return trackId;
    }, [trackData, token, updateTrackData, navigate, location]);

    return saveTrack;
};
